// =============================================================
// FLOW (d + 9.2c): Spares inventory & warehouse ledger — the booking math +
// the operational-write tabs (goods receipt · write-off · transfer · Excel
// import preflight). Ported from scripts/perpetuum-desktop-sections.jsx
// (SecInventory), now reading + WRITING the REAL /api/v1.
//
// Available = In stock − Booked (core rule). The anti-fraud rules fire
// SERVER-side and are surfaced honestly here:
//   - Book      → POST /spares/:id/book        (no over-book; 422)
//   - Receive   → POST /spares/:id/receive     (real-time-only intake)
//   - Write-off → POST /spares/:id/write-off   (non-negative cap; 422)
//   - Transfer  → POST /spares/:id/transfer    (only Available may move; 422)
//   - Import    → POST /import/spares/preflight (UPDATE/CREATE/SECURITY-VIOLATION)
// Each write re-reads the live levels (query invalidation) so the math is proven
// end-to-end against the backend, not a local store.
// =============================================================

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, availableQty } from "@perpetuum/core";
import {
  useBookSpare,
  useReceiveSpare,
  useSparesImportPreflight,
  useSpareLevels,
  useSpares,
  useStocks,
  useTransferSpare,
  useWriteOffSpare,
} from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { Skeleton } from "../components/Skeleton.js";
import { EmptyState } from "../components/EmptyState.js";
import type { SparePreflightReport } from "@perpetuum/core";

type Tab = "book" | "receipt" | "writeoff" | "transfer" | "import";

const selectStyle = {
  border: 0,
  background: "none",
  outline: "none",
  font: "inherit",
  color: "inherit",
  width: "100%",
} as const;

export function Inventory() {
  const t = useScopedT();
  const { can } = useAuth();
  const sparesQ = useSpares();
  const stocksQ = useStocks();

  const spares = sparesQ.data ?? [];
  const stocks = stocksQ.data ?? [];
  // The spare/stock pickers (and the booking math) depend on these two lists;
  // show skeleton rows while they load instead of rendering empty selects.
  const loading = sparesQ.isLoading || stocksQ.isLoading;
  const [tab, setTab] = useState<Tab>("book");
  const [spareId, setSpareId] = useState<string | null>(null);
  const [stockId, setStockId] = useState<string | null>(null);

  useEffect(() => {
    if (!spareId && spares[0]) setSpareId(spares[0].id);
  }, [spares, spareId]);
  useEffect(() => {
    const main = stocks.find((s) => s.isMain) ?? stocks[0];
    if (!stockId && main) setStockId(main.id);
  }, [stocks, stockId]);

  const levels = useSpareLevels(spareId);
  const level = useMemo(() => (levels.data ?? []).find((l) => l.stockId === stockId), [levels.data, stockId]);
  const inStock = level?.inStock ?? 0;
  const booked = level?.booked ?? 0;
  const available = availableQty(inStock, booked);
  const spare = spares.find((s) => s.id === spareId);

  const TABS: [Tab, string][] = [
    ["book", "inventory.tab.book"],
    ["receipt", "inventory.tab.receipt"],
    ["writeoff", "inventory.tab.writeoff"],
    ["transfer", "inventory.tab.transfer"],
    ["import", "inventory.tab.import"],
  ];

  const spareSelect = (
    <div className="d-field">
      <label htmlFor="spare">{t("inventory.item")}</label>
      <div className="field-ctrl">
        <select id="spare" value={spareId ?? ""} onChange={(e) => setSpareId(e.target.value)} style={selectStyle}>
          {spares.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code ? `${s.code} · ` : ""}
              {s.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const stockSelect = (
    <div className="d-field">
      <label htmlFor="stock">{t("dc.location")}</label>
      <div className="field-ctrl">
        <select id="stock" value={stockId ?? ""} onChange={(e) => setStockId(e.target.value)} style={selectStyle}>
          {stocks.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.isMain ? " (main)" : ""}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const stockMath = (label: string) => (
    <div className="stock-math" data-testid="stock-math">
      <div className="sm-row">
        <span className="sm-k">{t("inventory.inStock")}</span>
        <span className="sm-v" data-testid="in-stock">
          {inStock} pcs
        </span>
      </div>
      <div className="sm-row">
        <span className="sm-k">
          {t("inventory.booked")} <small>(reserved on open work orders)</small>
        </span>
        <span className="sm-v warn" data-testid="booked">
          − {booked} pcs
        </span>
      </div>
      <div className="sm-row total">
        <span className="sm-k">{label}</span>
        <span className="sm-v ok" data-testid="available">
          {available} pcs
        </span>
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="d-h2">{t("inventory.title")}</h2>
      <p className="d-sub">{t("inventory.sub")}</p>

      <div className="view-seg" style={{ marginBottom: 18 }} role="tablist" aria-label="Ledger actions">
        {TABS.map(([k, labelKey]) => (
          <button key={k} role="tab" aria-pressed={tab === k} aria-selected={tab === k} onClick={() => setTab(k)}>
            {t(labelKey)}
          </button>
        ))}
      </div>

      {loading && tab !== "import" ? (
        <div className="d-card" style={{ maxWidth: 520 }} data-testid="inventory-loading">
          <div className="d-card-body">
            <Skeleton variant="text" count={6} />
          </div>
        </div>
      ) : !loading && tab !== "import" && spares.length === 0 ? (
        <div className="d-card" style={{ maxWidth: 520 }}>
          <div className="d-card-body" style={{ padding: 0 }}>
            <EmptyState icon={ICONS.inbox} title={t("inventory.noSpares")} hint={t("inventory.noSparesHint")} compact />
          </div>
        </div>
      ) : (
        <>
          {tab === "book" && <BookCard {...{ spareSelect, stockSelect, stockMath, spareId, stockId, available, inStock, booked, spareName: spare?.name }} />}
          {tab === "receipt" && <ReceiptCard {...{ spareSelect, stockSelect, spareId, stockId, spareName: spare?.name }} />}
          {tab === "writeoff" && <WriteOffCard {...{ spareSelect, stockSelect, stockMath, spareId, stockId, available, inStock, booked, spareName: spare?.name }} />}
          {tab === "transfer" && (
            <TransferCard {...{ spareSelect, stockSelect, stockMath, spareId, fromStockId: stockId, stocks, available, spareName: spare?.name }} />
          )}
          {tab === "import" && <ImportCard />}
        </>
      )}

      {!can("spares", "write") && (
        <div className="inline-alert warn" style={{ maxWidth: 520, marginTop: 14 }} data-testid="ro-note">
          <Icon d={ICONS.lock} size={15} />
          {t("inventory.readonly")}
        </div>
      )}
    </div>
  );
}

// ---- Book (existing) ------------------------------------------------------

function BookCard(props: {
  spareSelect: ReactNode;
  stockSelect: ReactNode;
  stockMath: (label: string) => ReactNode;
  spareId: string | null;
  stockId: string | null;
  available: number;
  spareName?: string;
}) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const book = useBookSpare();
  const [qty, setQty] = useState("1");
  const n = parseInt(qty, 10);
  const overbook = !isNaN(n) && n > props.available;

  const onBook = async () => {
    if (!props.spareId || !props.stockId || isNaN(n) || n <= 0) return;
    try {
      await book.mutateAsync({ spareId: props.spareId, stockId: props.stockId, quantity: n });
      toast(t("inventory.booked.ok", { n, name: props.spareName ?? "" }), "success");
    } catch (e) {
      if (e instanceof ApiError && (e.code === "overbook" || e.status === 422)) toast(t("inventory.overbook"), "warning");
      else toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div className="d-card" style={{ maxWidth: 520 }}>
      <div className="d-card-hd">
        <h3>{t("inventory.book")}</h3>
      </div>
      <div className="d-card-body">
        {props.spareSelect}
        {props.stockSelect}
        {props.stockMath(t("inventory.available"))}
        <div className="d-field">
          <label htmlFor="qty">{t("inventory.qty")}</label>
          <div className={`field-ctrl ${overbook ? "is-error" : ""}`}>
            <input id="qty" type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          {overbook && (
            <div className="inline-alert warn" data-testid="overbook-warn">
              {t("inventory.overbook")}
            </div>
          )}
        </div>
        <Btn
          variant="primary"
          size="core"
          data-testid="book-btn"
          disabled={!can("spares", "write") || isNaN(n) || n <= 0 || overbook || book.isPending}
          onClick={() => void onBook()}
        >
          {t("inventory.confirm")}
        </Btn>
      </div>
    </div>
  );
}

// ---- Goods receipt (real-time only) ---------------------------------------

function ReceiptCard(props: {
  spareSelect: ReactNode;
  stockSelect: ReactNode;
  spareId: string | null;
  stockId: string | null;
  spareName?: string;
}) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const receive = useReceiveSpare();
  const [qty, setQty] = useState("1");
  const [supplier, setSupplier] = useState("");
  const [docNr, setDocNr] = useState("");
  const n = parseInt(qty, 10);

  const onReceive = async () => {
    if (!props.spareId || !props.stockId || isNaN(n) || n <= 0) return;
    try {
      await receive.mutateAsync({ spareId: props.spareId, stockId: props.stockId, quantity: n, supplier: supplier || undefined, docNr: docNr || undefined });
      toast(t("inventory.received.ok", { n, name: props.spareName ?? "" }), "success");
      setQty("1");
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) toast(t("inventory.receiptRealtime"), "warning");
      else toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div className="d-card" style={{ maxWidth: 560 }}>
      <div className="d-card-hd">
        <h3>{t("inventory.receiptTitle")}</h3>
      </div>
      <div className="d-card-body">
        {props.spareSelect}
        {props.stockSelect}
        <div className="d-field">
          <label htmlFor="supplier">{t("inventory.supplier")}</label>
          <div className="field-ctrl">
            <input id="supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="AMUT Ecotech S.p.A." />
          </div>
        </div>
        <div className="d-field">
          <label htmlFor="docNr">{t("inventory.docNr")}</label>
          <div className="field-ctrl">
            <input id="docNr" value={docNr} onChange={(e) => setDocNr(e.target.value)} placeholder="DN-2026-0418" />
          </div>
        </div>
        <div className="d-field">
          <label>{t("inventory.intakeTs")}</label>
          <div className="lock-note">
            <Icon d={ICONS.lock} size={14} />
            {t("inventory.intakeNote")}
          </div>
        </div>
        <div className="d-field">
          <label htmlFor="rqty">{t("inventory.qtyReceived")}</label>
          <div className="field-ctrl">
            <input id="rqty" type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
            <span className="suffix-badge">pcs</span>
          </div>
        </div>
        <Btn
          variant="primary"
          size="core"
          data-testid="receive-btn"
          disabled={!can("spares", "write") || isNaN(n) || n <= 0 || receive.isPending}
          onClick={() => void onReceive()}
        >
          <Icon d={ICONS.plus} size={16} />
          {t("inventory.registerIntake")}
        </Btn>
      </div>
    </div>
  );
}

// ---- Write-off (non-negative cap) -----------------------------------------

function WriteOffCard(props: {
  spareSelect: ReactNode;
  stockSelect: ReactNode;
  stockMath: (label: string) => ReactNode;
  spareId: string | null;
  stockId: string | null;
  available: number;
  inStock: number;
  booked: number;
  spareName?: string;
}) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const writeOff = useWriteOffSpare();
  const [qty, setQty] = useState("");
  const n = parseInt(qty, 10);
  // The server caps at on-hand (in stock). We warn when the input exceeds it.
  const capped = !isNaN(n) && n > props.inStock;

  const onWriteOff = async () => {
    if (!props.spareId || !props.stockId || isNaN(n) || n <= 0) return;
    try {
      await writeOff.mutateAsync({ spareId: props.spareId, stockId: props.stockId, quantity: n });
      toast(t("inventory.wroteOff.ok", { n, name: props.spareName ?? "" }), "success");
      setQty("");
    } catch (e) {
      if (e instanceof ApiError && (e.code === "writeoff_exceeds_stock" || e.status === 422)) toast(t("inventory.writeoffCap"), "warning");
      else toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div className="d-card" style={{ maxWidth: 520 }}>
      <div className="d-card-hd">
        <h3>{t("inventory.writeoffTitle")}</h3>
      </div>
      <div className="d-card-body">
        {props.spareSelect}
        {props.stockSelect}
        {props.stockMath(t("inventory.available"))}
        <div className="d-field">
          <label htmlFor="woqty">{t("inventory.qtyWriteOff")}</label>
          <div className={`field-ctrl ${capped ? "is-error" : ""}`}>
            <input id="woqty" type="number" min={1} placeholder="0" value={qty} onChange={(e) => setQty(e.target.value)} />
            <span className="suffix-badge">pcs</span>
          </div>
          {capped && (
            <div className="inline-alert warn" data-testid="writeoff-cap-warn">
              {t("inventory.writeoffCapHint", { inStock: props.inStock })}
            </div>
          )}
        </div>
        <Btn
          variant="primary"
          size="core"
          data-testid="writeoff-btn"
          disabled={!can("spares", "write") || isNaN(n) || n <= 0 || writeOff.isPending}
          onClick={() => void onWriteOff()}
        >
          {t("inventory.confirmWriteOff")}
        </Btn>
      </div>
    </div>
  );
}

// ---- Transfer (stock → stock, booking-aware) ------------------------------

function TransferCard(props: {
  spareSelect: ReactNode;
  stockSelect: ReactNode;
  stockMath: (label: string) => ReactNode;
  spareId: string | null;
  fromStockId: string | null;
  stocks: { id: string; name: string; isMain?: boolean }[];
  available: number;
  spareName?: string;
}) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const transfer = useTransferSpare();
  const [toStockId, setToStockId] = useState<string>("");
  const [qty, setQty] = useState("1");
  const n = parseInt(qty, 10);
  const over = !isNaN(n) && n > props.available;

  // Default the destination to a stock different from the source.
  useEffect(() => {
    if (!toStockId) {
      const dest = props.stocks.find((s) => s.id !== props.fromStockId);
      if (dest) setToStockId(dest.id);
    }
  }, [props.stocks, toStockId, props.fromStockId]);

  const onTransfer = async () => {
    if (!props.spareId || !props.fromStockId || !toStockId || isNaN(n) || n <= 0) return;
    try {
      await transfer.mutateAsync({ spareId: props.spareId, fromStockId: props.fromStockId, toStockId, quantity: n });
      toast(t("inventory.transferred.ok", { n, name: props.spareName ?? "" }), "success");
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) toast(t("inventory.transferCap"), "warning");
      else toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div className="d-card" style={{ maxWidth: 560 }}>
      <div className="d-card-hd">
        <h3>{t("inventory.transferTitle")}</h3>
      </div>
      <div className="d-card-body">
        {props.spareSelect}
        {/* Source = the shared stock dropdown (its levels feed the math below). */}
        {props.stockSelect}
        <div className="d-field">
          <label htmlFor="toStock">{t("inventory.toWarehouse")}</label>
          <div className="field-ctrl">
            <select id="toStock" value={toStockId} onChange={(e) => setToStockId(e.target.value)} style={selectStyle}>
              {props.stocks
                .filter((s) => s.id !== props.fromStockId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        {props.stockMath(t("inventory.availTransfer"))}
        <div className="d-field">
          <label htmlFor="tqty">{t("inventory.qty")}</label>
          <div className={`field-ctrl ${over ? "is-error" : ""}`}>
            <input id="tqty" type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} />
            <span className="suffix-badge">pcs</span>
          </div>
          {over && (
            <div className="inline-alert warn" data-testid="transfer-cap-warn">
              {t("inventory.transferCapHint", { available: props.available })}
            </div>
          )}
        </div>
        <div className="note" style={{ marginBottom: 14 }}>
          <Icon d={ICONS.alert} size={15} />
          <div>{t("inventory.transferNote")}</div>
        </div>
        <Btn
          variant="primary"
          size="core"
          data-testid="transfer-btn"
          disabled={!can("spares", "write") || isNaN(n) || n <= 0 || over || transfer.isPending}
          onClick={() => void onTransfer()}
        >
          <Icon d={ICONS.refresh} size={16} />
          {t("inventory.confirmTransfer")}
        </Btn>
      </div>
    </div>
  );
}

// ---- Excel import preflight (UPDATE / CREATE / SECURITY-VIOLATION) ---------

const TAG_CLASS: Record<string, string> = { CREATE: "create", UPDATE: "update", "SECURITY-VIOLATION": "violation" };

function ImportCard() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const preflight = useSparesImportPreflight();
  const [text, setText] = useState(
    "AST-1180-04\tGranulator screen Ø40\nAST-2210-09\tV-belt SPB 1800\nAST-9920-XX\tUnknown carbide assembly\n\tPelletiser cutter blade (new)",
  );
  const [report, setReport] = useState<SparePreflightReport | null>(null);

  const onRun = async () => {
    // Parse pasted TSV/CSV lines: "<alldeviceId><tab|comma><name>".
    const rows = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [id, ...rest] = line.split(/\t|,/);
        return { alldeviceId: (id ?? "").trim(), name: rest.join(" ").trim() };
      });
    try {
      const res = await preflight.mutateAsync(rows);
      setReport(res);
      toast(t("inventory.preflightDone", { n: res.summary.total }), "success");
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };

  return (
    <div className="d-card" style={{ maxWidth: 720 }}>
      <div className="d-card-hd">
        <h3>{t("inventory.importTitle")}</h3>
        <span style={{ flex: 1 }} />
        <span className="row-tag" style={{ background: "var(--status-warning-bg)", color: "var(--status-warning-ink)" }}>
          <Icon d={ICONS.alert} size={14} />
          {t("inventory.importLimit")}
        </span>
      </div>
      <div className="d-card-body">
        <div className="d-field">
          <label htmlFor="import-text">{t("inventory.importPaste")}</label>
          <textarea
            id="import-text"
            data-testid="import-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            style={{ width: "100%", font: "inherit", fontFamily: "var(--font-mono, monospace)", fontSize: 13, padding: 10, borderRadius: 8, border: "1px solid var(--sys-border-2)", background: "var(--sys-bg-inset)", color: "var(--sys-fg-1)" }}
          />
        </div>
        <Btn variant="primary" size="core" data-testid="preflight-btn" disabled={!can("spares", "write") || preflight.isPending} onClick={() => void onRun()}>
          {t("inventory.runPreflight")}
        </Btn>

        {report && (
          <table className="d-table" data-testid="preflight-table" style={{ marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ width: 180 }}>ID</th>
                <th style={{ width: 240 }}>{t("inventory.validation")}</th>
                <th>{t("inventory.reason")}</th>
              </tr>
            </thead>
            <tbody>
              {report.verdicts.map((v, i) => (
                <tr key={i} className="rowhover">
                  <td className="mono">{v.alldeviceId || <span style={{ color: "var(--sys-fg-mute)" }}>— blank —</span>}</td>
                  <td>
                    <span className={`row-tag ${TAG_CLASS[v.tag] ?? ""}`} data-testid={`verdict-${v.tag}`}>
                      {v.tag}
                    </span>
                  </td>
                  <td style={{ color: "var(--sys-fg-3)", fontSize: 12.5 }}>{v.reason ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {report && (
          <p className="d-sub" data-testid="preflight-summary" style={{ marginTop: 12 }}>
            {t("inventory.preflightSummary", { create: report.summary.create, update: report.summary.update, violation: report.summary.securityViolation })}
          </p>
        )}
      </div>
    </div>
  );
}
